import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { usePageMeta } from '../lib/usePageMeta';

const LAST_UPDATED = '2026-05-12';

const TOC = [
  { id: 'acceptance', label: '1. Acceptance of terms' },
  { id: 'service', label: '2. The service' },
  { id: 'eligibility', label: '3. Eligibility and accounts' },
  { id: 'pricing', label: '4. Pricing, payments, and refunds' },
  { id: 'ip-content', label: '5. Your content and intellectual property' },
  { id: 'ai-disclosure', label: '6. AI-generated content disclosure' },
  { id: 'acceptable-use', label: '7. Acceptable use' },
  { id: 'termination', label: '8. Termination' },
  { id: 'warranties', label: '9. Disclaimers and warranties' },
  { id: 'liability', label: '10. Limitation of liability' },
  { id: 'indemnification', label: '11. Indemnification' },
  { id: 'changes', label: '12. Changes to the service or these terms' },
  { id: 'governing-law', label: '13. Governing law' },
  { id: 'contact', label: '14. Contact' },
];

export function Terms() {
  usePageMeta({
    title: 'Terms of Service | StoryMint',
    description:
      'StoryMint Terms of Service: the rules that govern your use of our AI-powered novel-writing service.',
    canonical: 'https://storymint.io/terms',
    ogType: 'website',
  });

  const handleTocClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="public" />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <header className="mb-10">
          <div className="text-xs font-bold text-primary tracking-[0.12em] uppercase mb-3">
            Legal
          </div>
          <h1 className="text-[clamp(2rem,5vw,3.25rem)] font-extrabold text-foreground mb-4 leading-[1.15] tracking-tight">
            Terms of Service
          </h1>
          <p className="text-sm text-muted-foreground">
            Last updated:{' '}
            <time dateTime={LAST_UPDATED}>
              {new Date(`${LAST_UPDATED}T00:00:00Z`).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
          </p>
        </header>

        <nav
          aria-label="Table of contents"
          className="p-6 rounded-xl border border-border bg-card mb-12"
        >
          <h2 className="text-sm font-bold text-foreground tracking-wide uppercase mb-4">
            Contents
          </h2>
          <ol className="space-y-2 text-sm">
            {TOC.map((entry) => (
              <li key={entry.id}>
                <a
                  href={`#${entry.id}`}
                  onClick={(e) => handleTocClick(e, entry.id)}
                  className="text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                >
                  {entry.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-10 text-foreground text-[17px] leading-[1.8]">
          <section id="acceptance" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              1. Acceptance of terms
            </h2>
            <p className="text-muted-foreground">
              By creating an account on StoryMint, accessing the StoryMint website, or
              using any feature of the service, you agree to be bound by these Terms of
              Service. If you do not agree, do not use the service.
            </p>
            <p className="text-muted-foreground">
              These terms form a binding agreement between you and StoryMint
              (&ldquo;StoryMint,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;). They apply
              every time you use the service, including future updates and features.
            </p>
          </section>

          <section id="service" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              2. The service
            </h2>
            <p className="text-muted-foreground">
              StoryMint is a software-as-a-service platform that generates fiction
              manuscripts from a user-supplied premise using large language models and
              related AI tooling. The service includes story-bible memory across chapters,
              cover-image generation, manuscript export in EPUB and DOCX formats, and
              guidance for self-publishing on Amazon Kindle Direct Publishing.
            </p>
            <p className="text-muted-foreground">
              We reserve the right to add, modify, or remove features at any time. We will
              make reasonable effort to notify you of material changes that affect
              functionality you actively use.
            </p>
          </section>

          <section id="eligibility" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              3. Eligibility and accounts
            </h2>
            <p className="text-muted-foreground">
              You must be at least 13 years old to create an account. If you are under
              the age of majority in your jurisdiction, you may only use the service with
              the involvement of a parent or legal guardian.
            </p>
            <p className="text-muted-foreground">
              You are responsible for: (a) the accuracy of the information you provide
              when creating your account; (b) keeping your account credentials
              confidential; and (c) any activity that occurs under your account. Notify
              us immediately at the contact address below if you suspect your account has
              been compromised.
            </p>
            <p className="text-muted-foreground">
              You may use the service through a pen name. Each StoryMint account may
              maintain multiple author personas; you are responsible for the legality of
              the names you choose.
            </p>
          </section>

          <section id="pricing" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              4. Pricing, payments, and refunds
            </h2>
            <p className="text-muted-foreground">
              Paid plans are billed monthly in advance. Current pricing is shown at{' '}
              <a href="/pricing" className="text-primary hover:underline">
                storymint.io/pricing
              </a>{' '}
              and may change with reasonable notice for renewing periods.
            </p>
            <p className="text-muted-foreground">
              <strong className="text-foreground">Payment processor.</strong> We use
              Stripe to process payments. Your card details are handled by Stripe and are
              never stored on our servers. By providing payment information, you also
              agree to Stripe&apos;s terms.
            </p>
            <p className="text-muted-foreground">
              <strong className="text-foreground">Refunds.</strong> Monthly subscriptions
              are non-refundable for partial periods. If you cancel mid-month, your plan
              remains active until the end of the billing period. We may at our
              discretion grant refunds for service disruptions or billing errors. Credit
              packs are non-refundable once any credit in the pack has been consumed.
            </p>
            <p className="text-muted-foreground">
              <strong className="text-foreground">Failed payments.</strong> If a renewal
              payment fails, we may suspend access until the balance is paid. Generated
              content remains in your account during suspension; only generation of new
              content is paused.
            </p>
          </section>

          <section id="ip-content" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              5. Your content and intellectual property
            </h2>
            <p className="text-muted-foreground">
              <strong className="text-foreground">You own what you create.</strong> The
              novels, characters, world-building details, and cover artwork generated for
              you on StoryMint are yours. StoryMint does not claim ownership over your
              output. You are free to publish, sell, modify, and distribute it under your
              own name or pen name.
            </p>
            <p className="text-muted-foreground">
              You grant StoryMint a limited license to host, process, transmit, and
              display your content solely as necessary to operate the service. This
              license terminates when you delete the content or close your account,
              except where retention is required by law or for legitimate operational
              reasons (e.g. fraud investigation, backup retention).
            </p>
            <p className="text-muted-foreground">
              The StoryMint name, logo, brand assets, website design, and underlying
              source code remain the exclusive property of StoryMint. These terms do not
              grant you any license to those assets.
            </p>
          </section>

          <section id="ai-disclosure" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              6. AI-generated content disclosure
            </h2>
            <p className="text-muted-foreground">
              StoryMint generates content using third-party large language models
              (currently including those from Anthropic and OpenAI) and image-generation
              models (currently DALL-E 3). AI output:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>May contain factual inaccuracies, anachronisms, or implausible details</li>
              <li>May occasionally reproduce phrases or stylistic patterns common to its training data</li>
              <li>Should be reviewed and edited by you before publication</li>
            </ul>
            <p className="text-muted-foreground">
              <strong className="text-foreground">We do not train on your content.</strong>{' '}
              Your premises, manuscripts, and account information are not used to train,
              fine-tune, or otherwise improve the underlying AI models. We send your
              prompts to model providers under their no-retention API agreements where
              such options exist.
            </p>
            <p className="text-muted-foreground">
              <strong className="text-foreground">Disclosure to publishers.</strong> If you
              publish AI-assisted work through retailers that require disclosure (such as
              Amazon KDP), you are responsible for following their disclosure rules. We
              provide guidance but do not represent you to those retailers.
            </p>
          </section>

          <section id="acceptable-use" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              7. Acceptable use
            </h2>
            <p className="text-muted-foreground">You agree not to use the service to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Generate content that sexually exploits minors, or that we determine in good faith depicts minors in a sexual context</li>
              <li>Generate content intended to harass, defame, or threaten a specific real person</li>
              <li>Generate content that incites violence, terrorism, or self-harm</li>
              <li>Attempt to extract, redistribute, or resell the AI models, prompts, or system instructions used by StoryMint</li>
              <li>Reverse-engineer, scrape, or place automated workload on the service beyond the rate limits applicable to your plan</li>
              <li>Use the service to violate any applicable law or third-party right</li>
            </ul>
            <p className="text-muted-foreground">
              Violation of these rules may result in immediate suspension or termination
              of your account without refund.
            </p>
          </section>

          <section id="termination" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              8. Termination
            </h2>
            <p className="text-muted-foreground">
              You may close your account at any time from your account settings or by
              contacting us. Upon closure, your access ends at the conclusion of the
              current billing period.
            </p>
            <p className="text-muted-foreground">
              We may suspend or terminate your account immediately if you violate these
              terms, if continued service would expose us to legal risk, or if your
              account is dormant for an extended period (notified in advance).
            </p>
            <p className="text-muted-foreground">
              You can export your manuscripts before termination. After 30 days from
              termination, we may delete content stored on our systems beyond legal
              retention requirements.
            </p>
          </section>

          <section id="warranties" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              9. Disclaimers and warranties
            </h2>
            <p className="text-muted-foreground uppercase text-xs font-semibold tracking-wider">
              The service is provided on an &ldquo;as-is&rdquo; and &ldquo;as-available&rdquo; basis.
            </p>
            <p className="text-muted-foreground">
              To the maximum extent permitted by law, we disclaim all warranties, express
              or implied, including merchantability, fitness for a particular purpose,
              non-infringement, and any warranty arising out of course of dealing or use
              of trade.
            </p>
            <p className="text-muted-foreground">
              We do not warrant that the service will be uninterrupted, error-free, or
              free from harmful components. We do not warrant that AI-generated content
              will be accurate, marketable, free of prior art conflicts, or otherwise
              suitable for any particular use.
            </p>
          </section>

          <section id="liability" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              10. Limitation of liability
            </h2>
            <p className="text-muted-foreground">
              To the maximum extent permitted by law, StoryMint and its officers, agents,
              and employees shall not be liable for any indirect, incidental, special,
              consequential, or punitive damages, including loss of profits, data, or
              goodwill.
            </p>
            <p className="text-muted-foreground">
              Our aggregate liability arising out of or relating to these terms or the
              service is limited to the greater of (a) the amount you paid to StoryMint
              in the twelve months preceding the claim, or (b) one hundred US dollars.
            </p>
          </section>

          <section id="indemnification" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              11. Indemnification
            </h2>
            <p className="text-muted-foreground">
              You agree to indemnify and hold StoryMint harmless from any claim, demand,
              loss, or expense (including reasonable attorney fees) arising out of your
              use of the service, your content, or your violation of these terms or any
              law or third-party right.
            </p>
          </section>

          <section id="changes" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              12. Changes to the service or these terms
            </h2>
            <p className="text-muted-foreground">
              We may modify the service, our pricing, or these terms from time to time.
              Material changes will be posted on this page with the &ldquo;Last
              updated&rdquo; date adjusted, and where the change is significant we will
              notify active subscribers by email. Continued use of the service after the
              effective date constitutes acceptance of the modified terms.
            </p>
          </section>

          <section id="governing-law" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              13. Governing law
            </h2>
            <p className="text-muted-foreground">
              These terms are governed by the laws of [STATE / JURISDICTION], without
              regard to its conflict-of-laws provisions. Any dispute arising out of or
              relating to these terms or the service will be resolved exclusively in the
              state or federal courts located in [VENUE].
            </p>
          </section>

          <section id="contact" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              14. Contact
            </h2>
            <p className="text-muted-foreground">
              Questions about these terms, account closures, refund requests, or other
              legal notices can be sent to{' '}
              <a
                href="mailto:legal@storymint.io"
                className="text-primary hover:underline"
              >
                legal@storymint.io
              </a>
              .
            </p>
          </section>
        </div>
      </article>

      <Footer />
    </div>
  );
}
